import { useLocation, useNavigate } from "react-router-dom";

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const useUrlParams = (defaultParams: Record<string, any>) => {
  const navigate = useNavigate();
  const query = useQuery();

  const serialize = (params: Record<string, any>) => {
    return Object.entries(params).reduce(
      (acc, [key, value]) => {
        acc[key] = JSON.stringify(value);
        return acc;
      },
      {} as Record<string, string>
    );
  };

  const deserialize = (params: Record<string, any>) => {
    return Object.entries(params).reduce(
      (acc, [key, value]) => {
        try {
          acc[key] = JSON.parse(value);
        } catch {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, any>
    );
  };

  const setUrlParams = (params: Record<string, any>) => {
    const searchParams = new URLSearchParams(query);
    const serializedParams = serialize(params);

    Object.entries(serializedParams).forEach(([key, value]) => {
      const defaultValue = defaultParams[key];
      if (
        value !== undefined &&
        value !== "null" &&
        value !== "[]" &&
        value !== "" &&
        value !== JSON.stringify(defaultValue)
      ) {
        searchParams.set(key, value);
      } else {
        searchParams.delete(key);
      }
    });
    navigate({ search: searchParams.toString() }, { replace: true });
  };

  const getInitialParams = () => {
    const urlParams = {} as Record<string, any>;

    Object.keys(defaultParams).forEach(key => {
      const value = query.get(key);
      if (value !== null) {
        urlParams[key] = value;
      }
    });

    const params = { ...defaultParams, ...deserialize(urlParams) };
    const validate = validateParams(params);
    if (validate) {
      return params;
    } else {
      return defaultParams;
    }
  };

  const validateParams = (params: Record<string, any>) => {
    return Object.entries(params).every(([key, value]) => {
      if (
        typeof value === "string" &&
        (value === "" || value === "null" || value === "[]" || value === "{}")
      ) {
        return true;
      }
      if (typeof value === "object" && Array.isArray(value)) {
        return true;
      }
      try {
        JSON.parse(value);
        return true;
      } catch {
        console.error(`Invalid value for ${key}: ${value}`);
        return false;
      }
    });
  };

  return { setUrlParams, getInitialParams };
};

export default useUrlParams;
